import type { Middleware } from 'instantsearch.js';
import type { InsightsProps } from 'instantsearch.js/es/middlewares';

/**
 * InstantSearch `insights` options that send each list of viewed hits once.
 * Every hits widget sends its hits as a view on each render, so without this
 * the same list reaches Algolia several times for one search.
 */
export const createInsightsOptions = (userToken?: string): InsightsProps => {
  const sentViews = new Set<string>();

  return {
    // instantsearch.js types these init params as a tuple, not as the options object it reads.
    insightsInitParams: {
      userToken,
    } as unknown as InsightsProps['insightsInitParams'],
    onEvent: (event, insightsClient) => {
      if (!event.insightsMethod || !insightsClient) return;

      if (event.eventType === 'view') {
        const { index, objectIDs } = event.payload as unknown as {
          index: string;
          objectIDs: string[];
        };
        const viewKey = `${index}:${objectIDs.join(',')}`;
        if (sentViews.has(viewKey)) return;
        sentViews.add(viewKey);
      }

      const algoliaSource = ['instantsearch'];
      if (event.eventModifier === 'internal') {
        algoliaSource.push('instantsearch-internal');
      }

      insightsClient(event.insightsMethod, {
        ...event.payload,
        algoliaSource,
      } as never);
    },
  };
};

/**
 * Middleware for InstantSearch instances rendered with `insights={false}`: it
 * hands the filter clicks that refinement widgets emit to `onFilterClick`.
 * The insights middleware would send them too, but it also changes the
 * listing's search parameters (clickAnalytics and userToken).
 */
export const createFilterClickMiddleware =
  (onFilterClick: (index: string, filters: string[]) => void): Middleware =>
  ({ instantSearchInstance }) => {
    let previousSendEventToInsights:
      | typeof instantSearchInstance.sendEventToInsights
      | undefined;

    return {
      $$type: 'breitling.filterClicks',
      started: () => {
        const hasInsightsMiddleware = instantSearchInstance.middleware.some(
          ({ instance }) => instance.$$type === 'ais.insights',
        );
        if (hasInsightsMiddleware) return;

        previousSendEventToInsights = instantSearchInstance.sendEventToInsights;
        instantSearchInstance.sendEventToInsights = (event) => {
          if (event.insightsMethod !== 'clickedFilters') return;

          const { filters, index } = event.payload as unknown as {
            filters: string[];
            index: string;
          };
          onFilterClick(index, filters);
        };
      },
      unsubscribe: () => {
        if (previousSendEventToInsights) {
          instantSearchInstance.sendEventToInsights =
            previousSendEventToInsights;
        }
      },
    };
  };
